import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Divider,
  Button,
  Typography,
  Stack,
  Tooltip,
  IconButton
} from "@mui/material";
import Chart from "react-apexcharts";
import moment from "moment";
import * as XLSX from "xlsx";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SingleSelect from "../../../components/Selections/singleSelect";
import { useData } from "../../../context/DataContext";
import MaterialReactTableWithSearch from "../../../components/MaterialReactTable/MaterialReactTableWithSearch";
import LoadingComponent from "../../../components/Loading/LoadingComponent";
import CustomButton from "../../../components/buttons/customButton";

function TrainingReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const { rowData } = location.state || {};
  const scrollRef = useRef(null);

  const {
    trainingReport = [],
    subLoading,
    individualFarmersDetails,
    handleIndividualFarmersDetails,
    trainingTypes = [],
  } = useData();

  const [trainingTypeId, setTrainingTypeId] = useState(null);
  const [selectedChatType, setSelectedChatType] = useState(1);
  const [selectedBtnType, setselectedBtnType] = useState(11);
  const [filteredTrainingReport, setFilteredTrainingReport] = useState([]);

  /* ---------------- NORMALIZE API DATA ---------------- */
  const normalizedData = useMemo(() => {
    return trainingReport.reduce((acc, item) => {
      acc[item.type] = item.data;
      return acc;
    }, {});
  }, [trainingReport]);

  const sessionsData = normalizedData.trainingSessions || [];
  const reportData = normalizedData.trainingReport || [];
  const attendanceData = normalizedData.trainingAttendance || {};

  /* ---------------- TRAINING TYPE ---------------- */
  useEffect(() => {
    if (trainingTypes.length && rowData?.training_type_id) {
      setTrainingTypeId(rowData.training_type_id);
    }
  }, [trainingTypes, rowData]);

  /* ---------------- ACTIVE TABLE DATA ---------------- */
  const activeTableData = useMemo(() => {
    if (selectedChatType === 1) return sessionsData;
    if (selectedChatType === 2) return reportData;
    return [];
  }, [selectedChatType, sessionsData, reportData]);

  /* ---------------- DEFAULT FILTER ---------------- */
  useEffect(() => {
    if (selectedChatType === 1) {
      setFilteredTrainingReport(
        sessionsData.filter(i => i.training_status_id !== null)
      );
      setselectedBtnType(11);
    }
    if (selectedChatType === 2) {
      setFilteredTrainingReport(
        reportData.filter(i => i.training_status_id === 2)
      );
      setselectedBtnType(21);
    }
  }, [selectedChatType, sessionsData, reportData]);

  /* ---------------- COUNTS ---------------- */
  const totalscheduledCount = sessionsData?.length || 0;
  const scheduledCount = sessionsData.filter(i => i.training_status_id !== null).length;
  const unscheduledCount = sessionsData.filter(i => i.training_status_id === null).length;
  const totalScheduledLabel = rowData.level_id === 1 ? "Total Villages" : `Total Groups `;

  const inprocessCount = reportData.filter(i => i.training_status_id === 2).length;
  const completedCount = reportData.filter(i => i.training_status_id === 3).length;
  const pendingCount = reportData.filter(i => i.training_status_id === 1).length;

  const {
    total_count = 0,
    total = 0,
    present = 0,
    absent = 0,
    Not_Attended = 0,
  } = attendanceData;


  /* ---------------- TABLE COLUMNS ---------------- */
  const trainingsHistoryColumn = useMemo(() => {
    const cols = [
      { accessorFn: (_, i) => i + 1, header: "SNO", size: 40 },
      rowData?.level_id === 1
        ? { accessorKey: "village_name", header: "Village Name" }
        : { accessorKey: "group_name", header: "Group Name" },
      { accessorKey: "training_type", header: "Training Type" },
      { accessorKey: "name", header: "FF Name" },
      { accessorKey: "mobile_no", header: "Mobile" },
      {
        accessorKey: "session_date",
        header: "Session Date",
        Cell: ({ row }) =>
          row.original.session_date
            ? moment.unix(row.original.session_date).format("DD/MM/YYYY")
            : "-"
      },
    ];

    if (selectedChatType === 2) {
      if (trainingTypeId === 1) {
        cols.push(
          { accessorKey: "total_count", header: "Total Farmers" },
          { accessorKey: "attended", header: "Present" },
          { accessorKey: "absent", header: "Absent" },
          { accessorKey: "Not_Attended", header: "Not Attended", }
        );
      } else {
        cols.push(
          { accessorKey: "total_count", header: "Attended Count" },
        );
      }
    }

    return cols;
  }, [rowData, selectedChatType]);

  /* ---------------- FILTER BUTTON CLICK ---------------- */
  const handleTypeBtnClick = (type) => {
    const filters = {
      11: i => i.training_status_id !== null,
      12: i => i.training_status_id === null,
      21: i => i.training_status_id === 2,
      22: i => i.training_status_id === 3,
      23: i => i.training_status_id === 1,
    };
    setFilteredTrainingReport(activeTableData.filter(filters[type]));
    setselectedBtnType(type);
  };

  /* ---------------- CHAT CLICK ---------------- */
  const handleChatClick = (type) => {
    setSelectedChatType(type);
  };

  /* ---------------- EXCEL DOWNLOAD ---------------- */
  const handleExcelDownload = async (row) => {
    const rowData = row.original;
    if (!rowData.training_session_id) return;

    await handleIndividualFarmersDetails(rowData.training_session_id);

    if (!individualFarmersDetails.length) return;

    const ws = XLSX.utils.json_to_sheet(individualFarmersDetails);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Farmers");
    XLSX.writeFile(wb, "farmers_data.xlsx");
  };

  /* ---------------- CHART DATA ---------------- */
  const donut = (series, labels, totalLabel) => ({
    series,
    options: {
      chart: {
        type: "donut",
      },
      labels,
      legend: {
        position: "right",
        formatter: (label, opts) =>
          `${label} (${opts.w.globals.series[opts.seriesIndex]})`,
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`,
      },
      tooltip: {
        y: {
          formatter: (val) => val,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                show: true,
                label: totalLabel,
                formatter: (w) =>
                  w.globals.seriesTotals.reduce((a, b) => a + b, 0),
              },
            },
          },
        },
      },
    },
  });


  const chartCards = [
    {
      id: 1,
      label: "Sessions",
      clickId: 1,
      data: donut(
        [totalscheduledCount, scheduledCount, unscheduledCount],
        [`${totalScheduledLabel}`, "Scheduled", "Unscheduled"]
      ),
      showNoData:
        scheduledCount === 0 || unscheduledCount === 0,
    },
    {
      id: 2,
      label: "Report",
      clickId: 2,
      data: donut(
        [totalscheduledCount, inprocessCount, completedCount, pendingCount],
        [`${totalScheduledLabel}`, "InProcess", "Completed", "Pending"]
      ),
      showNoData:
        inprocessCount === 0 || completedCount === 0
    },
    {
      id: 3,
      label: "Attendance",
      data: trainingTypeId === 1 ? donut(
        [total_count, total, present, absent, Not_Attended],
        ["Total Farmers", "Attended Farmers", "Present", "Absent", "Not Attended"]
      ) : donut(
        [total_count],
        ["Attended Count"]
      ),
      showNoData:
        scheduledCount === 0 || unscheduledCount === 0,
    },
  ];

  const handleInputChange = (name, value) => {
    if (name === 'trainingTypeId') {
      setTrainingTypeId(value);
    }
  }

  if (subLoading) return <LoadingComponent />;


  const NoDataUI = () => (
    <Box
      sx={{
        height: 170,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
      }}
    >
      <BarChartOutlinedIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
      <Typography variant="subtitle1" fontWeight={600}>
        No Data Found
      </Typography>
      <Typography variant="caption">
        Nothing to visualize yet
      </Typography>
    </Box>
  );

  return (
    <Box>

      <Card elevation={6}>

        <Grid container alignItems="center" mt={1} mx={1}>
          <Grid item size={{ xs: 12, md: 1 }}>
            <CustomButton
              label="Back"
              status={false}
              onClick={() => navigate(-1)}
              icon={<ArrowBackIcon />}
            />
          </Grid>

          <Grid item size={{ xs: 12, md: 8 }} textAlign="center">
            <Typography variant="h5" textTransform="uppercase">
              Training Report
            </Typography>
          </Grid>

          <Grid item size={{ xs: 12, md: 3 }} display="flex" justifyContent="flex-end">
            <SingleSelect
              name="trainingTypeId"
              label="Select Type"
              options={trainingTypes.filter((item) => +item.value === rowData?.training_type_id)}
              value={trainingTypeId}
              onChange={handleInputChange}
              disabled
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 1, bgcolor: '#f5f1f1ff' }} />

        <CardContent>
          <Grid container spacing={2}>
            {chartCards.map(card => (
              <Grid
                size={{ xs: 12, md: 4 }}
                key={card.id}
                onClick={() => card.clickId && handleChatClick(card.clickId)}
                sx={{
                  boxShadow: 3,
                  cursor: card.clickId ? "pointer" : "default",
                  border:
                    selectedChatType === card.clickId
                      ? "2px solid #1D5E40"
                      : "2px solid transparent",
                  borderRadius: 1,
                  textAlign: "center",
                  "&:hover": {
                    border: card.clickId ? "2px solid #1D5E40" : "none",
                  },
                }}
              >
                {/* <Chart {...card.data} type="donut" height={170} /> */}

                {card.showNoData ? (
                  <NoDataUI />
                ) : (
                  <Chart {...card.data} type="donut" height={170} />
                )}
                <Typography textAlign="center" fontWeight="bold">
                  {card.label}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Buttons with counts */}
          <Box ref={scrollRef}
            sx={{
              overflowX: "auto",
              whiteSpace: "nowrap",
              scrollBehavior: "smooth",
              width: "100%",
              px: 1, mb: 2,
              "&::-webkit-scrollbar": { display: "none" },
            }}>
            <Stack direction="row" spacing={1} mt={2} ref={scrollRef}>
              {(selectedChatType === 1
                ? [
                  { label: "Scheduled", value: 11, count: scheduledCount },
                  { label: "UnScheduled", value: 12, count: unscheduledCount },
                ]
                : [
                  { label: "InProcess", value: 21, count: inprocessCount },
                  { label: "Completed", value: 22, count: completedCount },
                  { label: "Pending", value: 23, count: pendingCount },
                ]
              ).map(btn => (
                <Button
                  key={btn.value}
                  variant={selectedBtnType === btn.value ? "contained" : "outlined"}
                  color="success"
                  onClick={() => handleTypeBtnClick(btn.value)}
                  sx={{
                    textTransform: "none", borderRadius: 2,
                    "&:hover": {
                      backgroundColor: selectedBtnType === btn.value
                        ? "success.dark"
                        : "success.main",
                      color: "#fff",
                      borderColor: "success.main",
                    },
                  }}
                >
                  {btn.label} ({btn.count})
                </Button>
              ))}
            </Stack>
          </Box>

          {/* Table */}
          <Grid container spacing={1}>
            <Grid item size={{ xs: 12, md: 12 }} sx={{ boxShadow: 6 }}>
              <MaterialReactTableWithSearch
                data={filteredTrainingReport}
                columns={trainingsHistoryColumn}
                enableRowActions={selectedChatType === 2}
                renderRowActions={({ row }) =>
                  selectedChatType === 2 ? (
                    <Tooltip title="Download Excel">
                      <IconButton
                        disabled={!row.original.training_session_id}
                        onClick={() => handleExcelDownload(row)}
                      >
                        <BarChartOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  ) : null
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}

export default TrainingReport;
